import { useState } from "react";
import { DateTime } from "luxon";
import { Line, Bar } from "react-chartjs-2";
import type { ChartData, ChartOptions } from "chart.js";
import type { TrendSeries } from "@/hooks/useTrendData";
import type { Granularity } from "@/lib/periods";
import type { FilterState } from "@/types/filters";
import { getCSSVar } from "@/lib/chart";
import { Card } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

const GRAN_LABELS = { day: "Daily", week: "Weekly", month: "Monthly" } as const;

const TOGGLE_ITEM_CLS =
  "h-auto text-[12px] px-3 py-1 border-black/15 bg-white text-gray-600 font-normal hover:bg-gray-50 hover:text-gray-600 transition-colors data-[state=on]:bg-[var(--brand-dark-teal)] data-[state=on]:text-white data-[state=on]:border-[var(--brand-dark-teal)] data-[state=on]:z-10";

function resolveColors() {
  return {
    seen: getCSSVar("--perf-green") || "#52aa52",
    other: getCSSVar("--brand-mid-teal") || "#3fa0ab",
    avail: getCSSVar("--perf-gray") || "#b8b6b2",
    block: getCSSVar("--perf-red") || "#e05252",
  };
}

type LegendKey = "seen" | "other" | "avail" | "block";
type ChartMode = "pct" | "hrs";

interface UtilizationChartProps {
  trend: TrendSeries;
  granularity: FilterState["utilGranularity"];
  // Groupings the selected period actually spans — a chart with one point
  // is not a trend, so the rest are disabled rather than hidden.
  availableGranularities: Granularity[];
  // True when the period is calendar-based and grouped by week, so the chart
  // is showing fewer days than the KPI cards above it.
  partialWeeksShown: boolean;
  setUtilGranularity: (g: FilterState["utilGranularity"]) => void;
  typeFilterActive?: boolean;
}

export function UtilizationChart({
  trend,
  granularity,
  availableGranularities,
  partialWeeksShown,
  setUtilGranularity,
  typeFilterActive,
}: UtilizationChartProps) {
  const [colors] = useState(resolveColors);
  const [chartMode, setChartMode] = useState<ChartMode>("pct");

  // Day and week labels carry two parts ("7/27 Mon", "W31 7/27") — split onto
  // two lines so Chart.js keeps them horizontal at wider ranges instead of
  // rotating or dropping most of them. Month labels are a single part.
  const labels =
    granularity === "month"
      ? trend.current.map((p) => p.label)
      : trend.current.map((p) => p.label.split(" "));

  const pctLegendItems: Array<{ key: LegendKey; label: string; dash?: string }> = [
    { key: "seen", label: "Pets seen %", dash: undefined },
    ...(typeFilterActive
      ? [{ key: "other" as const, label: "Other appt types %", dash: "8 3" }]
      : []),
    { key: "block", label: "Block-off %", dash: "2 2" },
    { key: "avail", label: "Available time %", dash: "5 3" },
  ];

  const hrsLegendItems: Array<{ key: LegendKey; label: string }> = [
    { key: "seen", label: "Pets seen hrs" },
    ...(typeFilterActive ? [{ key: "other" as const, label: "Other appt types hrs" }] : []),
    { key: "block", label: "Block-off hrs" },
    { key: "avail", label: "Available time hrs" },
  ];

  // --- Percentage line chart ---
  const pctData: ChartData<"line"> = {
    labels,
    datasets: [
      {
        label: "Pets seen %",
        data: trend.current.map((p) => +(p.seenRate * 100).toFixed(1)),
        borderColor: colors.seen,
        backgroundColor: `${colors.seen}10`,
        fill: true,
        tension: 0.35,
        pointRadius: 3,
        pointBackgroundColor: colors.seen,
        borderWidth: 2,
      },
      ...(typeFilterActive
        ? [
            {
              label: "Other appt types %",
              data: trend.current.map((p) => +(p.otherRate * 100).toFixed(1)),
              borderColor: colors.other,
              backgroundColor: "transparent",
              fill: false,
              tension: 0.35,
              pointRadius: 3,
              pointBackgroundColor: colors.other,
              borderWidth: 2,
              borderDash: [8, 3],
            },
          ]
        : []),
      {
        label: "Block-off %",
        data: trend.current.map((p) => +(p.blockoffRate * 100).toFixed(1)),
        borderColor: colors.block,
        backgroundColor: "transparent",
        fill: false,
        tension: 0.35,
        pointRadius: 3,
        pointBackgroundColor: colors.block,
        borderWidth: 2,
        borderDash: [2, 2],
      },
      {
        label: "Available time %",
        data: trend.current.map((p) => +(p.availRate * 100).toFixed(1)),
        borderColor: colors.avail,
        backgroundColor: "transparent",
        fill: false,
        tension: 0.35,
        pointRadius: 3,
        pointBackgroundColor: colors.avail,
        borderWidth: 2,
        borderDash: [5, 3],
      },
    ],
  };

  const pctOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const point = trend.current[items[0]?.dataIndex ?? -1];
            if (!point) return items[0]?.label ?? "";
            return granularity === "day"
              ? DateTime.fromISO(point.bucketKey).toFormat("ccc M/d")
              : point.label;
          },
          // parsed.y is null for a gap in the series; Chart.js types it that way
          // because a dataset may legitimately skip a point.
          label: (ctx) =>
            `${ctx.dataset.label}: ${ctx.parsed.y === null ? "—" : `${ctx.parsed.y.toFixed(1)}%`}`,
        },
      },
    },
    scales: {
      y: {
        min: 0,
        ticks: { font: { size: 11 }, color: "#6b6b68", callback: (v) => `${v}%` },
        grid: { color: "#e5e4e0" },
      },
      x: {
        // Never rotate — drop labels instead so they stay horizontal at any
        // range width. Chart.js's default is to rotate before it skips.
        ticks: { font: { size: 11 }, color: "#6b6b68", maxRotation: 0, minRotation: 0 },
        grid: { display: false },
      },
    },
  };

  // --- Hours stacked bar chart ---
  const hrsData: ChartData<"bar"> = {
    labels,
    datasets: [
      {
        label: "Pets seen hrs",
        data: trend.current.map((p) => +(p.seenRate * p.avgHoursPerDvmPerDay).toFixed(2)),
        backgroundColor: colors.seen,
        stack: "hours",
      },
      ...(typeFilterActive
        ? [
            {
              label: "Other appt types hrs",
              data: trend.current.map((p) => +(p.otherRate * p.avgHoursPerDvmPerDay).toFixed(2)),
              backgroundColor: colors.other,
              stack: "hours",
            },
          ]
        : []),
      {
        label: "Block-off hrs",
        data: trend.current.map((p) => +(p.blockoffRate * p.avgHoursPerDvmPerDay).toFixed(2)),
        backgroundColor: colors.block,
        stack: "hours",
      },
      {
        label: "Available time hrs",
        data: trend.current.map(
          (p) => +(Math.max(0, p.availRate) * p.avgHoursPerDvmPerDay).toFixed(2)
        ),
        backgroundColor: colors.avail,
        stack: "hours",
      },
    ],
  };

  const hrsOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const point = trend.current[items[0]?.dataIndex ?? -1];
            if (!point) return items[0]?.label ?? "";
            return granularity === "day"
              ? DateTime.fromISO(point.bucketKey).toFormat("ccc M/d")
              : point.label;
          },
          label: (ctx) =>
            `${ctx.dataset.label}: ${ctx.parsed.y === null ? "—" : `${ctx.parsed.y.toFixed(2)} hrs`}`,
        },
      },
    },
    scales: {
      y: {
        stacked: true,
        ticks: { font: { size: 11 }, color: "#6b6b68", callback: (v) => `${v}h` },
        grid: { color: "#e5e4e0" },
      },
      x: {
        stacked: true,
        ticks: { font: { size: 11 }, color: "#6b6b68", maxRotation: 0, minRotation: 0 },
        grid: { display: false },
      },
    },
  };

  const legendItems = chartMode === "pct" ? pctLegendItems : hrsLegendItems;

  return (
    <Card className="px-5 py-4 gap-0 shadow-none border-black/10">
      {/* Title row: title left, legend box right */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-[16px] font-medium">Utilization trend</div>
        <Card className="flex-row items-center gap-4 px-3 py-2 shadow-none border-black/10 rounded-lg">
          {legendItems.map(({ key, label }) => (
            <div key={key} className="flex items-center gap-1.5 text-[11px] text-gray-500">
              {chartMode === "pct" ? (
                <svg width="20" height="10" className="shrink-0">
                  <line
                    x1="0"
                    y1="5"
                    x2="20"
                    y2="5"
                    stroke={colors[key]}
                    strokeWidth="2"
                    strokeDasharray={
                      (pctLegendItems.find((i) => i.key === key) as { dash?: string } | undefined)
                        ?.dash
                    }
                  />
                </svg>
              ) : (
                <span
                  className="w-3 h-3 rounded-[2px] shrink-0 inline-block"
                  style={{ background: colors[key] }}
                />
              )}
              {label}
            </div>
          ))}
        </Card>
      </div>

      {/* Granularity label */}
      <div className="text-[12px] text-gray-500 mb-1">
        {chartMode === "pct" ? `${GRAN_LABELS[granularity]} (%)` : "Daily (Avg hours)"}
      </div>

      <div className="flex-1 min-h-0 relative">
        {chartMode === "pct" ? (
          <Line data={pctData} options={pctOptions} />
        ) : (
          <Bar data={hrsData} options={hrsOptions} />
        )}
      </div>

      {/* Bottom controls row */}
      <div className="flex items-center justify-between mt-3">
        {/* Chart mode toggle — bottom left */}
        <ToggleGroup
          type="single"
          variant="outline"
          value={chartMode}
          onValueChange={(v) => {
            if (v) setChartMode(v as ChartMode);
          }}
        >
          <ToggleGroupItem value="pct" className={TOGGLE_ITEM_CLS}>
            %
          </ToggleGroupItem>
          <ToggleGroupItem value="hrs" className={TOGGLE_ITEM_CLS}>
            Hours
          </ToggleGroupItem>
        </ToggleGroup>

        {/* Group by — bottom right */}
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-500 font-medium">Group by</span>
          <ToggleGroup
            type="single"
            variant="outline"
            value={granularity}
            onValueChange={(v) => {
              if (v) setUtilGranularity(v as FilterState["utilGranularity"]);
            }}
          >
            {(["day", "week", "month"] as const).map((g) => (
              <ToggleGroupItem
                key={g}
                value={g}
                disabled={!availableGranularities.includes(g)}
                className={TOGGLE_ITEM_CLS}
              >
                {g.charAt(0).toUpperCase() + g.slice(1)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>
      {partialWeeksShown && (
        <p className="text-[11px] text-gray-400 mt-2">
          The first and last points are part-weeks — a calendar period does not start or end on a
          Monday. Their totals are smaller for that reason; the per-DVM rates divide by the days
          actually elapsed, so those stay comparable.
        </p>
      )}
    </Card>
  );
}
